<template>
    <div class="report">
        <h1>Report a Sighting</h1>
        <ul v-if="errors.length" class="error">
            <li v-for="error in errors" v-bind:key="error">
                {{ error }}
            </li>
        </ul>
        <div>
            <form
                @submit.prevent
                accept-charset="UTF-8"
                enctype="multipart/form-data"
                novalidate
                v-if="isInitial || isSaving"
            >
                <div class="project split-ui">
                    <h2>Project</h2>
                    <div class="split">
                        <div class="ui">
                            <div>
                                <label for="project-group">Project Group</label>
                                <select
                                    id="project-group"
                                    v-model="projectGroup"
                                    @change="
                                        projectGroupChange($event.target.value)
                                    "
                                >
                                    <option value="">
                                        Select Project Group
                                    </option>
                                    <option
                                        v-for="option in projectGroups"
                                        v-bind:key="option.key"
                                        :value="option.id"
                                    >
                                        {{ option.name }}
                                    </option>
                                </select>
                            </div>
                            <div>
                                <label for="project" v-text="projectLabel">
                                </label>
                                <select
                                    id="project"
                                    v-model="project"
                                    :disabled="projectsDisabled"
                                    @change="
                                        projectChange(
                                            $event.target.selectedOptions[0]
                                        )
                                    "
                                >
                                    <option value="">Select Project</option>
                                    <option
                                        v-for="option in projects"
                                        v-bind:key="option.key"
                                        :value="option.species"
                                        v-bind:trackNumber="option.trackNumber"
                                    >
                                        {{ option.name }}
                                    </option>
                                </select>
                            </div>
                            <div
                                class="secondary-selections"
                                v-if="showSecondary"
                            >
                                <label>
                                    Additional Projects
                                </label>
                                <ul>
                                    <li
                                        v-for="(option,
                                        index) in secondaryProjects"
                                        v-bind:key="index"
                                    >
                                        <label :for="'project' + index">
                                            <input
                                                type="checkbox"
                                                :id="'project' + index"
                                                v-model="option.value"
                                                v-on:click="
                                                    toggleOption(option)
                                                "
                                            />
                                            {{ option.name }}
                                        </label>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div class="preview">
                            <p v-text="project"></p>
                        </div>
                    </div>
                </div>
                <div class="sighting-numbers split-ui" v-if="showNumber == 1">
                    <h2>Observations</h2>
                    <div class="split">
                        <div class="ui">
                            <div>
                                <label for="number">How Many</label>
                                <input
                                    v-model="number"
                                    type="number"
                                    id="number"
                                    name="number"
                                    min="0"
                                    placeholder="1"
                                />
                            </div>
                            <div class="hours-minutes">
                                <p>For how long?</p>
                                <label for="hours">Hours</label>
                                <input
                                    v-model="hours"
                                    type="number"
                                    id="hours"
                                    name="hours"
                                    min="0"
                                    placeholder="0"
                                />
                                <label for="hours">Minutes</label>
                                <input
                                    v-model="minutes"
                                    type="number"
                                    id="minutes"
                                    name="minutes"
                                    min="0"
                                    placeholder="0"
                                />
                            </div>
                        </div>
                        <div class="preview">
                            <p v-if="showNumber == 1">
                                Number observed:
                                <span v-text="number"></span>
                            </p>
                            <p v-if="showNumber == 1">
                                Observation duration:
                                <span v-text="hours"></span> hours,
                                <span v-text="minutes"></span> minutes
                            </p>
                        </div>
                    </div>
                </div>
                <div class="when split-ui">
                    <h2>Sighting Date</h2>
                    <div class="split">
                        <div class="ui">
                            <div>
                                <label for="when-month">When?</label>
                                <select
                                    v-model="whenMonth"
                                    id="when-month"
                                    @change="
                                        updateMonth(
                                            $event.target.selectedOptions[0]
                                        )
                                    "
                                >
                                    <option disabled value="">Year</option>
                                    <option
                                        v-for="option in monthOptions"
                                        v-bind:key="option.key"
                                        v-bind:value="option.value"
                                    >
                                        {{ option.text }}
                                    </option>
                                </select>
                                <select v-model="whenDay" id="when-day">
                                    <option disabled value="">Day</option>
                                    <option
                                        v-for="option in dayOptions"
                                        v-bind:key="option.key"
                                        v-bind:value="option.value"
                                    >
                                        {{ option.text }}
                                    </option>
                                </select>
                                <select v-model="whenYear" id="when-year">
                                    <option disabled value="">Year</option>
                                    <option
                                        v-for="option in yearOptions"
                                        v-bind:key="option.key"
                                        v-bind:value="option.value"
                                    >
                                        {{ option.text }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div class="preview">
                            <span v-text="month"></span>
                            &nbsp;<span v-text="whenDay"></span>,
                            <span v-text="whenYear"></span>
                        </div>
                    </div>
                </div>
                <div
                    id="location-ui"
                    class="location split-ui"
                    v-bind:class="{ dismissed: !this.showLocationTips }"
                >
                    <h2>
                        Location
                        <button
                            id="location-info"
                            class="info popup"
                            v-on:click="locationTipsModal"
                        >
                            ⓘ
                        </button>
                    </h2>
                    <div class="split">
                        <div class="ui">
                            <div id="location-instructions-modal" class="modal">
                                <button
                                    class="close-modal"
                                    v-on:click="closeModal"
                                >
                                    ⓧ
                                </button>
                                <div v-html="locationInstructions"></div>
                            </div>
                            <div
                                id="location-instructions"
                                class="tooltip instructions"
                            >
                                <button
                                    id="location-dismiss"
                                    class="dismiss"
                                    v-on:click="locationTipsDismiss"
                                >
                                    Dismiss
                                </button>
                                <div v-html="locationInstructions"></div>
                            </div>
                            <div>
                                <label for="latitude">Latitude</label>
                                <input
                                    v-model="latitude"
                                    type="text"
                                    id="latitude"
                                    name="latitude"
                                    value
                                    readonly
                                />
                            </div>
                            <div>
                                <label for="longitude">Longitude</label>
                                <input
                                    v-model="longitude"
                                    type="text"
                                    id="longitude"
                                    name="longitude"
                                    value
                                    readonly
                                />
                            </div>
                            <div>
                                <label for="address"
                                    >Search for an Address</label
                                >
                                <input
                                    v-model="address"
                                    type="text"
                                    id="address"
                                    name="address"
                                    @keydown.enter.prevent="doSearch"
                                />
                            </div>
                            <div>
                                <button type="button" v-on:click="doSearch">
                                    Search for Address
                                </button>
                            </div>
                        </div>
                        <div class="preview preview-map">
                            <div id="location-picker"></div>
                        </div>
                    </div>
                </div>
                <div
                    id="photo-ui"
                    class="photo-upload split-ui"
                    v-bind:class="{ dismissed: !this.showPhotoTips }"
                >
                    <h2>
                        Upload a Photo
                        <button
                            id="photo-tips-info"
                            class="info popup"
                            v-on:click="photoTipsModal"
                        >
                            ⓘ
                        </button>
                    </h2>
                    <div class="split">
                        <div class="ui">
                            <div id="photo-tips-modal" class="modal">
                                <button
                                    class="close-modal"
                                    v-on:click="closeModal"
                                >
                                    ⓧ
                                </button>
                                <div v-html="photoTips"></div>
                            </div>
                            <div
                                id="photo-tips-instructions"
                                class="tooltip instructions"
                            >
                                <button
                                    id="photo-tips-dismiss"
                                    class="dismiss"
                                    v-on:click="photoTipsDismiss"
                                >
                                    Dismiss
                                </button>
                                <div v-html="photoTips"></div>
                            </div>
                            <div class="file-drop">
                                <label for="photo">Photo</label>
                                <input
                                    type="file"
                                    id="photo"
                                    :name="uploadFieldName"
                                    :disabled="isSaving"
                                    @change="
                                        filesChange(
                                            $event.target.name,
                                            $event.target.files
                                        )
                                        fileCount = $event.target.files.length
                                    "
                                    accept="image/jpeg"
                                />
                                <p v-if="isInitial"></p>
                                <p v-if="isSaving && hasFiles">
                                    Uploading {{ fileCount }} files...
                                </p>
                            </div>
                        </div>
                        <div class="preview">
                            <p v-if="hasFiles">
                                Your photo will be displayed once it is
                                approved. Photos are typically reviewed and
                                approved during office hours, Monday through
                                Friday.
                            </p>
                        </div>
                    </div>
                </div>
                <div class="comments split-ui">
                    <h2>Additional Details</h2>
                    <div class="split">
                        <div class="ui">
                            <div>
                                <label for="comments">Comments</label>
                                <textarea
                                    v-model="comments"
                                    name="comments"
                                    id="comments"
                                >
                                </textarea>
                            </div>
                        </div>
                        <div class="preview">
                            <p v-text="comments"></p>
                        </div>
                    </div>
                </div>
                <div class="ui">
                    <div v-if="hasPhotoPermission" class="instructions">
                        <p>
                            You&rsquo;ve already authorized Journey North to use
                            posted material.
                        </p>
                        <input
                            v-model="hasPhotoPermission"
                            type="text"
                            name="hasPhotoPermission"
                            readonly
                            disabled
                            style="display: none;"
                        />
                    </div>
                    <div v-else class="instructions">
                        <p>
                            By submitting material (photographs, videos, or
                            text) to the Journey North website, you are
                            authorizing the University of Wisconsin-Madison to
                            use, and to authorize others to use, the material in
                            connection with the Journey North program.
                            UW-Madison may use, and may authorize others to use,
                            any material that you submit both on the Journey
                            North website as well as in publications or other
                            media, including but not limited to brochures, news
                            updates, newsletters, papers, magazine, television,
                            and websites, for educational, research, and
                            promotional purposes. By accepting these terms, you
                            are confirming that the material you are submitting
                            is your original material and that you have the
                            rights necessary to authorize the above uses of the
                            material.
                        </p>
                        <p>
                            You need only make this authorization once. We will
                            keep your acceptance on record.
                        </p>
                        <input
                            v-model="hasPhotoPermission"
                            type="text"
                            name="hasPhotoPermission"
                            readonly
                            disabled
                            style="display: none;"
                        />
                    </div>
                </div>
                <div>
                    <button type="submit" name="button" v-on:click="report">
                        Submit
                    </button>
                </div>
            </form>
            <div v-if="isSuccess">
                <h2>Sighting Posted</h2>
                <p>
                    <a href="javascript:void(0)" @click="reset()">
                        Post another sighting</a
                    ><br />
                    (We&rsquo;ll also have the sighting ID here to redirect to)
                </p>
            </div>
            <div v-if="isFailed">
                <h2>Post Failed</h2>
                <p>
                    <a href="javascript:void(0)" @click="reset()">Try again</a>
                </p>
                <p>{{ uploadError }}</p>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import L from 'leaflet-providers'

const STATUS_INITIAL = 0,
    STATUS_SAVING = 1,
    STATUS_SUCCESS = 2,
    STATUS_FAILED = 3
const FIELD_DISABLED = 0,
    FIELD_ENABLED = 1
let fd = new FormData()
const dateArray = new Date()
    .toISOString()
    .slice(0, 10)
    .split('-')

let locationTipsDismissed = localStorage.getItem('dismissLocationTips')
    ? true
    : false
let photoTipsDismissed = localStorage.getItem('dismissPhotoTips') ? true : false

export default {
    data() {
        return {
            showLocationTips: !locationTipsDismissed,
            showPhotoTips: !photoTipsDismissed,
            errors: [],
            uploadedFiles: [],
            uploadError: null,
            currentStatus: null,
            uploadFieldName: 'photo',
            customerId: '',
            email: '',
            firstName: '',
            lastName: '',
            projectLabel: 'Project',
            showSecondary: false,
            projectId: 0,
            project: '',
            secondaryProjects: [],
            secondaryProjectsToSave: [],
            rLatitude: '',
            rLongitude: '',
            latitude: '',
            longitude: '',
            address: '',
            marker: null,
            when: new Date().toISOString().slice(0, 10),
            comments: '',
            hasPhotoPermission: false,
            locationInstructions: '',
            photoTips: '',
            fileListLength: 0,
            number: 0,
            hours: 0,
            minutes: 0,
            showNumber: FIELD_DISABLED,

            displayMonth: '',
            whenMonth: dateArray[1],
            monthOptions: [
                { text: 'January', value: '01' },
                { text: 'February', value: '02' },
                { text: 'March', value: '03' },
                { text: 'April', value: '04' },
                { text: 'May', value: '05' },
                { text: 'June', value: '06' },
                { text: 'July', value: '07' },
                { text: 'August', value: '08' },
                { text: 'September', value: '09' },
                { text: 'October', value: '10' },
                { text: 'November', value: '11' },
                { text: 'December', value: '12' }
            ],
            whenDay: dateArray[2],
            dayOptions: [
                { text: '1', value: '01' },
                { text: '2', value: '02' },
                { text: '3', value: '03' },
                { text: '4', value: '04' },
                { text: '5', value: '05' },
                { text: '6', value: '06' },
                { text: '7', value: '07' },
                { text: '8', value: '08' },
                { text: '9', value: '09' },
                { text: '10', value: '10' },
                { text: '11', value: '11' },
                { text: '12', value: '12' },
                { text: '13', value: '13' },
                { text: '14', value: '14' },
                { text: '15', value: '15' },
                { text: '16', value: '16' },
                { text: '17', value: '17' },
                { text: '18', value: '18' },
                { text: '19', value: '19' },
                { text: '20', value: '20' },
                { text: '21', value: '21' },
                { text: '22', value: '22' },
                { text: '23', value: '23' },
                { text: '24', value: '24' },
                { text: '25', value: '25' },
                { text: '26', value: '26' },
                { text: '27', value: '27' },
                { text: '28', value: '28' },
                { text: '29', value: '29' },
                { text: '30', value: '30' },
                { text: '31', value: '31' }
            ],
            whenYear: dateArray[0],
            yearOptions: [
                { text: '2019', value: '2019' },
                { text: '2020', value: '2020' }
            ],

            projectGroup: '',
            projectGroups: [],
            projects: [],
            projectFieldState: FIELD_DISABLED
        }
    },
    created() {
        this.reset()

        const user = this.$store.state.user

        this.customerId = user.customerId
        this.email = user.email
        this.firstName = user.firstName
        this.lastName = user.lastName
        this.email = user.email
        this.rLatitude = user.latitude
        this.rLongitude = user.longitude
        this.latitude = user.latitude
        this.longitude = user.longitude
        this.hasPhotoPermission = user.photoPermission == 1 ? 'true' : 'false'

        switch (this.whenMonth) {
            case '01':
                this.displayMonth = 'January'
                break
            case '02':
                this.displayMonth = 'February'
                break
            case '03':
                this.displayMonth = 'March'
                break
            case '04':
                this.displayMonth = 'April'
                break
            case '05':
                this.displayMonth = 'May'
                break
            case '06':
                this.displayMonth = 'June'
                break
            case '07':
                this.displayMonth = 'July'
                break
            case '08':
                this.displayMonth = 'August'
                break
            case '09':
                this.displayMonth = 'September'
                break
            case '10':
                this.displayMonth = 'October'
                break
            case '11':
                this.displayMonth = 'November'
                break
            case '12':
                this.displayMonth = 'December'
                break
        }

        axios
            .get(process.env.VUE_APP_API_DOMAIN + '/projects/groups')
            .then(({ data }) => {
                this.projectGroups = data.projectGroups
            })

        axios
            .get(process.env.VUE_APP_DRUPAL_API_DOMAIN + '/api/photo-tips')
            .then(({ data }) => {
                // console.log(data[0].body[0])
                this.photoTips = data[0].body[0].value
            })

        axios
            .get(
                process.env.VUE_APP_DRUPAL_API_DOMAIN +
                    '/api/location-instructions'
            )
            .then(({ data }) => {
                // console.log(data[0].body[0])
                this.locationInstructions = data[0].body[0].value
            })

        let self = this
        if (navigator.geolocation) {
            var timeoutVal = 10 * 1000 * 1000
            navigator.geolocation.getCurrentPosition(
                function(position) {
                    self.latitude = position.coords.latitude
                    self.longitude = position.coords.longitude
                    console.log(self.latitude)
                    console.log(self.longitude)
                    self.initMap()
                    self.initLayers()
                },
                function(positionError) {
                    console.log(positionError)
                    self.initMap()
                    self.initLayers()
                },
                { enableHighAccuracy: true, timeout: timeoutVal, maximumAge: 0 }
            )
        } else {
            alert('Geolocation is not supported by this browser')
        }
    },
    computed: {
        isInitial() {
            return this.currentStatus === STATUS_INITIAL
        },
        isSaving() {
            return this.currentStatus === STATUS_SAVING
        },
        isSuccess() {
            return this.currentStatus === STATUS_SUCCESS
        },
        isFailed() {
            return this.currentStatus === STATUS_FAILED
        },
        hasFiles() {
            return this.fileListLength > 0
        },
        projectsEnabled() {
            return this.projectFieldState === FIELD_ENABLED
        },
        projectsDisabled() {
            return this.projectFieldState === FIELD_DISABLED
        },
        month() {
            // this.displayMonth = this.whenMonth
            return this.displayMonth
        }
    },
    methods: {
        reset() {
            // reset form to initial state
            this.currentStatus = STATUS_INITIAL
            this.uploadedFiles = []
            this.uploadError = null
            this.projectFieldState = FIELD_DISABLED

            let body = document.querySelector('body')
            let modalContainer = document.querySelector('.modal-container')
            if (!modalContainer) {
                modalContainer = document.createElement('div')
                modalContainer.classList.add('modal-container')
                body.appendChild(modalContainer)
            }
            while (modalContainer.lastChild) {
                modalContainer.removeChild(modalContainer.lastChild)
            }
        },
        appendModals() {
            // I had to fire this when the ⓘ are clicked because it doesn't
            // seem the template based modals were available when the "page"
            // is created. Might be best to just entirely build them on the fly
            // instead of in the template.
            let modalContainer = document.querySelector('.modal-container')
            let modals = modalContainer.querySelectorAll('.modal')
            console.log(modals)
            if (modals.length == 0) {
                let body = document.querySelector('body')
                modals = body.querySelectorAll('.modal')
            }
            Array.prototype.forEach.call(modals, function(el) {
                console.log(el)
                modalContainer.appendChild(el)
            })
        },
        closeModal() {
            let body = document.querySelector('body')
            body.classList.remove('show-modal')
            let modalContainer = document.querySelector('.modal-container')
            let modals = modalContainer.querySelectorAll('.modal')
            Array.prototype.forEach.call(modals, function(el) {
                el.classList.remove('visible')
            })
        },
        locationTipsDismiss() {
            let locationUi = document.getElementById('location-ui')
            locationUi.classList.add('dismissed')

            localStorage.setItem('dismissLocationTips', true)
            return false
        },
        photoTipsDismiss() {
            let photoUi = document.getElementById('photo-ui')
            photoUi.classList.add('dismissed')

            localStorage.setItem('dismissPhotoTips', true)
            return false
        },
        locationTipsModal() {
            this.appendModals()
            let body = document.querySelector('body')
            body.classList.add('show-modal')

            let locationInstructionsModal = document.getElementById(
                'location-instructions-modal'
            )
            locationInstructionsModal.classList.add('visible')
            return false
        },
        photoTipsModal() {
            this.appendModals()
            let body = document.querySelector('body')
            body.classList.add('show-modal')

            let photoTipsModal = document.getElementById('photo-tips-modal')
            photoTipsModal.classList.add('visible')
            return false
        },
        doSearch() {
            console.log(this.address)
            let self = this
            return axios
                .post(
                    process.env.VUE_APP_API_DOMAIN + '/geocode',
                    {
                        address: self.address
                    },
                    {}
                )
                .then(({ data }) => {
                    console.log(data)
                    if (data.position.lat != 0 && data.position.lng != 0) {
                        self.latitude = data.position.lat
                        self.longitude = data.position.lng
                        let newLatLng = new L.LatLng(
                            data.position.lat,
                            data.position.lng
                        )
                        self.marker.setLatLng(newLatLng)
                        self.map.panTo(newLatLng)
                    }
                })
                .catch(err => {
                    console.log(err)
                })
        },
        initMap() {
            this.map = L.map('location-picker', {
                zoom: 8,
                maxZoom: 12,
                center: [this.latitude, this.longitude],
                renderer: L.canvas(),
                zoomControl: true,
                scrollWheelZoom: false
            })

            L.tileLayer.provider('OpenTopoMap').addTo(this.map)
            L.tileLayer.provider('Esri.WorldTopoMap').addTo(this.map)

            var icon = L.icon({
                iconUrl: require('../assets/leaflet/marker-icon-2x-red.png'),
                iconSize: [25, 41],
                iconAnchor: [12, 41]
            })

            let self = this

            self.marker = L.marker([this.latitude, this.longitude], {
                icon: icon,
                title: 'Sighting',
                draggable: 'true'
            })

            self.marker.on('dragend', function(e) {
                var marker = e.target
                var position = marker.getLatLng()
                console.log(position)
                console.log(position.lat)
                marker.setLatLng(position, { draggable: 'true' }).update()
                self.latitude = position.lat
                self.longitude = position.lng
            })

            self.marker.addTo(this.map)

            // let popup = L.popup().setContent(
            // self.whenYear +
            // '-' +
            // self.whenMonth +
            // '-' +
            // self.whenDay +
            // '<br />' +
            // self.comments
            // )
            // This seems to obscure the draggable marker, which I worry will
            // cause confusion. There is no native leaflet.js solution to nudge
            // the popup so it doesn't obscure the marker.
            // self.marker.bindPopup(popup).openPopup()
        },
        updateBubble() {
            // let self = this
            // let popup = L.popup().setContent(
            //     self.whenYear +
            //         '-' +
            //         self.whenMonth +
            //         '-' +
            //         self.whenDay +
            //         '<br />' +
            //         self.comments
            // )
            // self.marker.bindPopup(popup).openPopup()
        },
        updateMonth(monthOption) {
            this.displayMonth = monthOption.text
        },
        initLayers() {},
        projectGroupChange(projectGroupId) {
            console.log(projectGroupId)
            this.projectFieldState = FIELD_DISABLED
            axios
                .get(
                    process.env.VUE_APP_API_DOMAIN +
                        '/projects/' +
                        projectGroupId
                )
                .then(({ data }) => {
                    this.projects = data.projects
                    if (this.projects.length > 1) {
                        this.project = ''
                        this.projectFieldState = FIELD_ENABLED
                    } else {
                        this.project = data.projects[0].species
                        this.projectFieldState = FIELD_DISABLED
                        if (data.projects[0].trackNumber == 1)
                            this.showNumber = FIELD_ENABLED
                        else this.showNumber = FIELD_DISABLED
                    }
                    if (data.showSecondary == 1) {
                        this.projectLabel = 'Primary Project'
                        this.showSecondary = true
                        this.secondaryProjects = data.projects
                        console.log(data)
                    } else {
                        this.projectLabel = 'Project'
                        this.showSecondary = false
                        this.secondaryProjects = []
                    }
                    // empty this out to reset what's sent to the server
                    this.secondaryProjectsToSave = []
                    console.log(this.projectsDisabled)
                })
        },
        toggleOption(option) {
            option.value = !option.value
            if (option.value) {
                // add to array
                console.log('add')
                this.secondaryProjectsToSave.push(option.id)
            } else {
                // remove from array
                console.log('remove')
                let i = 0
                while (i < this.secondaryProjectsToSave.length) {
                    if (this.secondaryProjectsToSave[i] === option.id) {
                        console.log('match')
                        this.secondaryProjectsToSave.splice(i, 1)
                    } else {
                        ++i
                    }
                }
            }
            console.log(this.secondaryProjectsToSave)
        },
        projectChange(trackNumber) {
            if (trackNumber.attributes['trackNumber'].value == 1)
                this.showNumber = FIELD_ENABLED
            else this.showNumber = FIELD_DISABLED
        },
        filesChange(fieldName, fileList) {
            if (!fileList.length) return

            this.fileListLength = fileList.length

            Array.from(Array(fileList.length).keys()).map(x => {
                fd.append(fieldName, fileList[x], fileList[x].name)
            })
        },
        report() {
            this.errors = []
            if (
                this.project.length == 0 ||
                this.project == '' ||
                this.project == null
            ) {
                this.errors.push('Project is a required field.')
            }
            if (
                this.latitude.length == 0 ||
                this.latitude == 0 ||
                this.latitude == null
            ) {
                this.errors.push('Latitude is a required field.')
            }
            if (
                this.longitude.length == 0 ||
                this.longitude == 0 ||
                this.longitude == null
            ) {
                this.errors.push('Longitude is a required field.')
            }
            if (
                this.comments.length == 0 ||
                this.comments == 0 ||
                this.comments == null
            ) {
                this.errors.push('Comments is a required field.')
            }
            if (this.errors.length > 0) {
                return false
            }

            this.currentStatus = STATUS_SAVING

            fd.append('customerId', this.customerId)
            fd.append('email', this.email)
            fd.append('firstName', this.firstName)
            fd.append('lastName', this.lastName)
            fd.append('projectId', this.projectId)
            fd.append('project', this.project)
            fd.append('latitude', this.latitude)
            fd.append('longitude', this.longitude)
            fd.append('number', this.number)
            fd.append('hours', this.hours)
            fd.append('minutes', this.minutes)
            fd.append(
                'when',
                this.whenYear + '-' + this.whenMonth + '-' + this.whenDay
            )
            fd.append('comments', this.comments)
            fd.append('hasPhotoPermission', this.hasPhotoPermission)
            fd.append('secondaryProjects', this.secondaryProjectsToSave)

            // this.file = this.$refs.file.files[0];
            // fd.append('photo', this.refs.multipartfiles.files[0])
            return axios
                .post(process.env.VUE_APP_API_DOMAIN + '/report', fd, {
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'multipart/form-data'
                    }
                })
                .then(({ data }) => {
                    // Handle 415 response
                    console.log(data)
                    // Handle success response
                    this.currentStatus = STATUS_SUCCESS
                })
                .catch(err => {
                    if (err.response.status == 415) {
                        this.uploadError =
                            'There was a problem with the image you uploaded. It must be a JPG photo and smaller than 8MB. Please try again.'
                    } else {
                        this.uploadError = 'There was a problem.'
                    }
                    // this.uploadError = err.response
                    this.currentStatus = STATUS_FAILED
                })
        }
    }
}
</script>

<style type="text/css">
#location-picker {
    background: gray;
    height: 400px;
    margin-right: auto;
    margin-left: auto;
    position: relative;
    width: 100%;
    z-index: 8;
}
</style>
